import React, { useEffect, useRef } from 'react';

export default function StarsBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let dpr = window.devicePixelRatio || 1;
    let width = 0, height = 0;
    let rafId = 0;
    let lastTime = performance.now();

    const stars = [];
    const shootingStars = [];
    let nextShootingAt = performance.now() + 600;

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildStarfield();
    };

    const buildStarfield = () => {
      stars.length = 0;
      const density = Math.floor((width * height) / 6000);
      for (let i = 0; i < density; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 1.2 + 0.2,
          a: Math.random() * 0.6 + 0.2,
          tw: Math.random() * 0.015 + 0.003,
          phase: Math.random() * Math.PI * 2,
        });
      }
    };

    const spawnShootingStar = () => {
      const angle = (Math.PI / 6) + (Math.random() * Math.PI / 6);
      const speed = 480 + Math.random() * 380;
      const startX = Math.random() * width * 0.7;
      const startY = -40 - Math.random() * 120;
      const life = 0.9 + Math.random() * 0.8;
      shootingStars.push({
        x: startX,
        y: startY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        age: 0,
        trail: [],
        size: 1.4 + Math.random() * 1.4,
      });
    };

    const drawStars = (t) => {
      for (const s of stars) {
        const tw = 0.6 + 0.4 * Math.sin(t * s.tw + s.phase);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${s.a * tw})`;
        ctx.fill();
      }
    };

    const updateAndDrawShooting = (dt) => {
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const s = shootingStars[i];
        s.age += dt;
        s.x += s.vx * dt;
        s.y += s.vy * dt;
        s.trail.push({ x: s.x, y: s.y, age: 0 });
        if (s.trail.length > 28) s.trail.shift();
        for (const p of s.trail) p.age += dt;

        const lifeT = s.age / s.life;
        const fade = lifeT < 0.85 ? 1 : Math.max(0, 1 - (lifeT - 0.85) / 0.15);

        // trail
        for (let j = 0; j < s.trail.length - 1; j++) {
          const p1 = s.trail[j];
          const p2 = s.trail[j + 1];
          const tAlpha = (j / s.trail.length);
          const alpha = tAlpha * 0.85 * fade;
          const w = (tAlpha * s.size * 1.8) + 0.2;
          const grad = ctx.createLinearGradient(p1.x, p1.y, p2.x, p2.y);
          grad.addColorStop(0, `rgba(255,255,255,0)`);
          grad.addColorStop(1, `rgba(255,235,255,${alpha})`);
          ctx.strokeStyle = grad;
          ctx.lineWidth = w;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.stroke();
        }

        // head glow
        const glow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.size * 8);
        glow.addColorStop(0, `rgba(255,255,255,${0.95 * fade})`);
        glow.addColorStop(0.3, `rgba(255,180,230,${0.45 * fade})`);
        glow.addColorStop(1, `rgba(255,180,230,0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size * 8, 0, Math.PI * 2);
        ctx.fill();

        // head core
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${fade})`;
        ctx.fill();

        if (s.age >= s.life || s.x > width + 100 || s.y > height + 100) {
          shootingStars.splice(i, 1);
        }
      }
    };

    const tick = (now) => {
      const dt = Math.min(0.05, (now - lastTime) / 1000);
      lastTime = now;

      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      ctx.fillRect(0, 0, width, height);

      drawStars(now * 0.001);

      if (now >= nextShootingAt) {
        spawnShootingStar();
        nextShootingAt = now + 800 + Math.random() * 2400;
      }
      updateAndDrawShooting(dt);

      rafId = requestAnimationFrame(tick);
    };

    resize();
    window.addEventListener('resize', resize);
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="stars-canvas" aria-hidden="true" />;
}
